import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Users, Building2, CalendarCheck, Zap, TrendingUp, Clock, Activity } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

export default function DashboardPage() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data: d } = await api.get("/dashboard"); setData(d); } catch {}
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64" data-testid="dashboard-loading"><div className="text-sm text-[#868E96]">{t("common.loading")}</div></div>;
  if (!data) return null;

  const kpis = data.kpis || {};
  const chartData = Object.entries(data.lead_statuses || {}).map(([name, value]) => ({ name: t(`status.${name}`, name), value }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard t={t} icon={Users} label={t("dashboard.total_leads")} value={kpis.total_leads} sub={t("dashboard.new_sub", { count: kpis.new_leads })} testId="kpi-total-leads" />
        <KpiCard t={t} icon={Building2} label={t("dashboard.properties")} value={kpis.total_properties} sub={t("dashboard.available_sub", { count: kpis.available_properties })} testId="kpi-properties" />
        <KpiCard t={t} icon={CalendarCheck} label={t("dashboard.visits")} value={kpis.total_visits} sub={t("dashboard.upcoming_sub", { count: kpis.scheduled_visits })} testId="kpi-visits" />
        <KpiCard t={t} icon={Zap} label={t("dashboard.matches")} value={kpis.total_matches} sub={t("dashboard.conversion_sub", { rate: kpis.conversion_rate })} testId="kpi-matches" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border bg-white p-5">
          <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96] mb-4">{t("dashboard.lead_status_chart")}</h3>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#868E96' }} />
                <YAxis tick={{ fontSize: 11, fill: '#868E96' }} />
                <Tooltip contentStyle={{ border: '1px solid #E9ECEF', borderRadius: 0, fontSize: 12 }} />
                <Bar dataKey="value" fill="#002FA7" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-[#868E96] py-8 text-center">{t("dashboard.no_lead_data")}</p>}
        </div>
        <div className="border border-border bg-white p-5 space-y-4">
          <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96]">{t("dashboard.quick_stats")}</h3>
          <div className="space-y-3">
            <StatRow icon={Users} label={t("dashboard.active_leads")} value={kpis.active_leads} />
            <StatRow icon={Clock} label={t("dashboard.dormant_leads")} value={kpis.dormant_leads} />
            <StatRow icon={CalendarCheck} label={t("dashboard.completed_visits")} value={kpis.completed_visits} />
            <StatRow icon={TrendingUp} label={t("dashboard.conversion_rate")} value={`${kpis.conversion_rate}%`} />
            <StatRow icon={Users} label={t("dashboard.team_size")} value={kpis.agents_count} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="border border-border bg-white p-5">
          <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96] mb-3">{t("dashboard.recent_matches")}</h3>
          {(data.recent_matches || []).length === 0 ? <p className="text-sm text-[#868E96] py-4 text-center">{t("dashboard.no_matches")}</p> : (
            <div className="space-y-2">
              {data.recent_matches.slice(0, 5).map((m, i) => (
                <div key={m.id || i} className="flex items-center justify-between py-2 border-b border-border last:border-0 animate-fade-in" style={{ animationDelay: `${i * 0.05}s` }}>
                  <div><p className="text-sm font-medium text-[#0A0A0A]">{m.client_name}</p><p className="text-xs text-[#868E96]">{m.property_title}</p></div>
                  <span className={`text-sm font-bold ${m.score >= 70 ? 'text-[#2B8A3E]' : m.score >= 50 ? 'text-[#E67700]' : 'text-[#868E96]'}`}>{m.score}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="border border-border bg-white p-5">
          <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96] mb-3">{t("dashboard.upcoming_visits")}</h3>
          {(data.upcoming_visits || []).length === 0 ? <p className="text-sm text-[#868E96] py-4 text-center">{t("dashboard.no_visits")}</p> : (
            <div className="space-y-2">
              {data.upcoming_visits.slice(0, 5).map((v, i) => (
                <div key={v.id || i} className="py-2 border-b border-border last:border-0 animate-fade-in" style={{ animationDelay: `${i * 0.05}s` }}>
                  <p className="text-sm font-medium text-[#0A0A0A]">{v.lead_name}</p>
                  <p className="text-xs text-[#868E96]">{v.property_title}</p>
                  <p className="text-xs text-[#002FA7] mt-0.5">{v.scheduled_at ? new Date(v.scheduled_at).toLocaleString() : ""}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="border border-border bg-white p-5">
          <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96] mb-3">{t("dashboard.recent_activity")}</h3>
          {(data.recent_activities || []).length === 0 ? <p className="text-sm text-[#868E96] py-4 text-center">{t("dashboard.no_activity")}</p> : (
            <div className="space-y-2">
              {data.recent_activities.slice(0, 5).map((a, i) => (
                <div key={a.id || i} className="flex items-start gap-2 py-2 border-b border-border last:border-0 animate-fade-in" style={{ animationDelay: `${i * 0.05}s` }}>
                  <Activity size={14} className="text-[#868E96] mt-0.5 shrink-0" />
                  <div><p className="text-sm text-[#0A0A0A]">{a.description}</p><p className="text-xs text-[#868E96]">{a.created_at ? new Date(a.created_at).toLocaleString() : ""}</p></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="kpi-card bg-white" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96]">{label}</span>
        <Icon size={16} className="text-[#002FA7]" strokeWidth={1.5} />
      </div>
      <p className="text-2xl font-black text-[#0A0A0A]" style={{ fontFamily: 'Chivo, sans-serif' }}>{value ?? 0}</p>
      {sub && <p className="text-xs text-[#868E96] mt-1">{sub}</p>}
    </div>
  );
}

function StatRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2"><Icon size={14} className="text-[#868E96]" strokeWidth={1.5} /><span className="text-sm text-[#495057]">{label}</span></div>
      <span className="text-sm font-semibold text-[#0A0A0A]">{value ?? 0}</span>
    </div>
  );
}
